/**
 * Web RadioGroup Component
 *
 * This is a legacy component.
 * For referencing while developing new features, please use a Functional component.
 */

import React from 'react'
import clsx from 'clsx'
import {
  extendPropsWithContextInClassComponent,
  makeUniqueId,
  validateDOMAttributes,
  getStatusState,
  combineDescribedBy,
  combineLabelledBy,
  dispatchCustomElementEvent,
} from '../../shared/component-helper'
import { pickFormElementProps } from '../../shared/helpers/filterValidProps'
import { createSpacingClasses } from '../space/SpacingHelper'
import AlignmentHelper from '../../shared/AlignmentHelper'
import Space from '../Space'
import FormLabel from '../FormLabel'
import FormStatus from '../FormStatus'
import Flex from '../Flex'
import Context from '../../shared/Context'
import Suffix from '../../shared/helpers/Suffix'
import RadioGroupContext from './RadioGroupContext'
import type {
  FormStatusBaseProps,
  FormStatusText,
  FormStatusState,
} from '../FormStatus'
import type { SkeletonShow } from '../Skeleton'
import type { SpacingProps } from '../space/types'
import withComponentMarkers from '../../shared/helpers/withComponentMarkers'

export type RadioGroupLabelPosition = 'left' | 'right'
export type RadioGroupSize = 'default' | 'medium' | 'large'
export type RadioGroupSuffix = string | React.ReactNode
export type RadioGroupLayoutDirection = 'column' | 'row'
export type RadioGroupChildren = string | React.ReactNode

export type RadioGroupChangeEvent = {
  value: string
  event: React.SyntheticEvent
}

export type RadioGroupProps = {
  label?: React.ReactNode
  labelDirection?: 'vertical' | 'horizontal'
  labelSrOnly?: boolean
  labelPosition?: RadioGroupLabelPosition
  title?: string
  disabled?: boolean
  skeleton?: SkeletonShow
  id?: string
  name?: string
  size?: RadioGroupSize
  status?: FormStatusText
  statusState?: FormStatusState
  statusProps?: FormStatusBaseProps
  statusNoAnimation?: boolean
  globalStatus?: FormStatusBaseProps['globalStatus']
  suffix?: RadioGroupSuffix
  vertical?: boolean
  layoutDirection?: RadioGroupLayoutDirection
  value?: string
  style?: React.CSSProperties
  className?: string
  children?: RadioGroupChildren
  onChange?: (event: RadioGroupChangeEvent) => void
} & SpacingProps

type RadioGroupComponentState = {
  value?: string
  _value?: string
  _listenForPropChanges: boolean
}

/**
 * The radio component is our enhancement of the classic radio button. It acts like a radio. Example: On/off, yes/no.
 */
export default class RadioGroup extends React.PureComponent<
  RadioGroupProps,
  RadioGroupComponentState
> {
  static contextType = Context
  context!: React.ContextType<typeof Context>

  _refInput: React.RefObject<HTMLInputElement | null>
  _id: string
  _name: string

  static defaultProps = {
    label: null,
    labelDirection: null,
    labelSrOnly: null,
    labelPosition: null,
    title: null,
    disabled: null,
    skeleton: null,
    id: null,
    name: null,
    size: null,
    status: null,
    statusState: 'error',
    statusProps: null,
    statusNoAnimation: null,
    globalStatus: null,
    suffix: null,
    vertical: null,
    layoutDirection: 'row',
    value: undefined,

    className: null,
    children: null,

    onChange: null,
  }

  static parseChecked = (state: string | boolean | null | undefined) =>
    /true|on/.test(String(state))

  static getDerivedStateFromProps(
    props: RadioGroupProps,
    state: RadioGroupComponentState
  ) {
    if (state._listenForPropChanges) {
      if (props.value !== state._value) {
        state.value = props.value
      }
      if (typeof props.value !== 'undefined') {
        state._value = props.value
      }
    }
    state._listenForPropChanges = true

    return state
  }

  constructor(props: RadioGroupProps) {
    super(props)
    this._refInput = React.createRef()
    this._id = props.id || makeUniqueId() // cause we need an id anyway
    this._name = props.name || this._id
    this.state = {
      _listenForPropChanges: true,
    }
  }

  onChangeHandler = ({
    value,
    event,
  }: {
    value: string
    event: React.SyntheticEvent
  }) => {
    this.setState({ value, _listenForPropChanges: false })
    dispatchCustomElementEvent(this, 'onChange', {
      value,
      event,
    })
  }

  render() {
    // use only the props from context, who are available here anyway
    const props = extendPropsWithContextInClassComponent(
      this.props,
      RadioGroup.defaultProps,
      pickFormElementProps(this.context?.formElement),
      (this.context as Record<string, unknown>)?.RadioGroup as
        | Record<string, unknown>
        | undefined
    )

    const {
      status,
      statusState,
      statusProps,
      statusNoAnimation,
      globalStatus,
      suffix,
      label,
      labelDirection,
      labelSrOnly,
      labelPosition,
      vertical,
      layoutDirection,
      size,
      disabled,
      skeleton,
      className,

      id: _id,
      name: _name,
      value: _value,
      children,
      onChange,

      ...rest
    } = props

    const { value } = this.state

    const id = this._id
    const showStatus = getStatusState(status)

    const classes = clsx(
      'dnb-radio-group',
      status && `dnb-radio-group__status--${statusState}`,
      `dnb-radio-group--${layoutDirection}`,
      'dnb-form-component',
      createSpacingClasses(props),
      className
    )

    const params = {
      ...rest,
    }
    const legendId = id + '-label'

    if (showStatus || suffix) {
      params['aria-describedby'] = combineDescribedBy(
        params,
        showStatus ? id + '-status' : null,
        suffix ? id + '-suffix' : null
      )
    }
    if (label) {
      params['aria-labelledby'] = combineLabelledBy(params, legendId)
    }

    // also used for code markup simulation
    validateDOMAttributes(this.props, params)

    const context = {
      name: this._name,
      value,
      size,
      disabled,
      labelPosition,
      onChange: this.onChangeHandler,
    }

    const Fieldset = label ? 'fieldset' : 'div'

    return (
      <RadioGroupContext value={context}>
        <div className={classes}>
          <AlignmentHelper />
          <Fieldset
            className="dnb-radio-group__fieldset"
            aria-labelledby={label ? legendId : undefined}
            role="radiogroup"
          >
            <Flex.Container
              direction={
                vertical || labelDirection === 'vertical'
                  ? 'vertical'
                  : 'horizontal'
              }
              gap={vertical ? 'x-small' : 'small'}
            >
              {label && (
                <FormLabel
                  element="legend"
                  id={legendId}
                  srOnly={labelSrOnly}
                >
                  {label}
                </FormLabel>
              )}

              <Space
                element="span"
                id={id}
                className="dnb-radio-group__shell"
                {...params}
              >
                {children as React.ReactNode}

                {suffix && (
                  <Suffix
                    className="dnb-radio-group__suffix"
                    id={id + '-suffix'} // used for "aria-describedby"
                    context={props}
                  >
                    {suffix as React.ReactNode}
                  </Suffix>
                )}

                <FormStatus
                  show={showStatus}
                  id={id + '-form-status'}
                  globalStatus={globalStatus}
                  label={label}
                  text={status}
                  state={statusState}
                  textId={id + '-status'} // used for "aria-describedby"
                  widthSelector={id + ', ' + legendId}
                  noAnimation={statusNoAnimation}
                  skeleton={skeleton}
                  {...statusProps}
                />
              </Space>
            </Flex.Container>
          </Fieldset>
        </div>
      </RadioGroupContext>
    )
  }
}

withComponentMarkers(RadioGroup, { _supportsSpacingProps: true })
